import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Toaster } from '@/components/ui/toaster';
import { StripeProvider } from '@/contexts/StripeContext';
import { AuthProvider, useAuth } from '@/contexts/SupabaseAuthContext';
import { CartProvider } from '@/hooks/useCart';
import { AccessibilityProvider } from '@/contexts/AccessibilityContext';
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import BuyCreditsPage from '@/pages/BuyCreditsPage';
import { CREDITS_VISIBLE } from '@/config/stripe';


// Components
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ShoppingCart from '@/components/ShoppingCart';
import ProtectedRoute from '@/components/ProtectedRoute';
import BuildPublicPage from '@/pages/BuildPublicPage';

// Pages - Core
// HeroSection removed - homepage now loads directly
import HomePage from '@/pages/HomePage';
import GoPage from '@/pages/GoPage';
import LoginPage from '@/components/LoginPage';
import StorePage from '@/pages/StorePage';
import ProductDetailPage from '@/pages/ProductDetailPage';
import CheckoutSuccessPage from '@/pages/CheckoutSuccessPage';
import LocationDetail from '@/pages/LocationDetail';
import HullPage from '@/pages/HullPage';
import RoomsPage from '@/pages/RoomsPage';
import EventsPage from '@/pages/EventsPage';
import AdminFundingDashboard from '@/components/AdminFundingDashboard';
import Dashboard from '@/pages/Dashboard';
import MaslowPage from '@/pages/MaslowPage';
import RevenueModelPage from '@/pages/RevenueModelPage';

// Restored imports
import ProfilePage from '@/pages/ProfilePage';
import ProfileSettingsPage from '@/pages/ProfileSettingsPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import FieldResearchPage from '@/pages/FieldResearchPage';
import FieldResearchResultsPage from '@/pages/FieldResearchResultsPage';
import FieldNotesIndex from '@/pages/FieldNotesIndex';
import FieldNotePost from '@/pages/FieldNotePost';
import AuthCallbackPage from '@/pages/AuthCallbackPage';
import BuildPage from '@/pages/BuildPage';

// Legal
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import SalesAgreementPage from '@/pages/SalesAgreementPage';
import DisclaimerPage from '@/pages/DisclaimerPage';
import CookiePolicyPage from '@/pages/CookiePolicyPage';
import FAQPage from '@/pages/FAQPage';

// Prototypes pages (admin)
import PrototypesPage from '@/pages/prototypes/PrototypesPage';
import PrototypeSystemDetailPage from '@/pages/prototypes/SystemDetailPage';
import PrototypeDetailPage from '@/pages/prototypes/PrototypeDetailPage';
import PrototypeShoppingCartPage from '@/pages/prototypes/ShoppingCartPage';
import PrototypeBoxViewPage from '@/pages/prototypes/BoxViewPage';
import PrototypeShoppingListPage from '@/pages/prototypes/ShoppingListPage';
import PresaleCheckoutPage from '@/pages/PresaleCheckoutPage';

// Scroll to top on every route change
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
};

const AppContent: React.FC = () => {
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isFounder, loading: authLoading, founderChecked } = useAuth();
  // Founder routes mount ONLY after the founder check resolves positive. While
  // auth is still resolving, the catch-all below renders a neutral blank screen
  // instead of redirecting — so a founder cold-loading a gated URL isn't bounced
  // to "/", and a logged-out visitor (or a JS-rendering crawler) never gets a
  // flash-render of gated pages (several carry pricing).
  const authResolving = authLoading || !founderChecked;
  const showFounderRoutes = founderChecked && isFounder;
  // Single switch for the presale page's public visibility. While false, /build
  // and /presale are founder-gated (preview only) and render with the global
  // Header. Flip to true to take the page public (standalone, headerless) in one
  // line — leave it false until the BuildPage copy is launch-ready.
  const BUILD_PAGE_PUBLIC = false;
  // Detect password recovery links and redirect to reset-password page
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('type=recovery')) {
      // Preserve the hash params and redirect to reset-password
      navigate('/reset-password' + hash, { replace: true });
    }
  }, [navigate]);

  // PRE-LAUNCH: homepage has its own self-contained nav. Auth routes are headerless.
  // /go (walk-up booking) keeps the global Header.
  // /build (+ /presale alias) renders headerless only when public; while gated it
  // keeps the global Header (founder preview).
  const isHideHeaderPath =
    location.pathname === '/' ||
    (BUILD_PAGE_PUBLIC &&
      (location.pathname === '/build' || location.pathname === '/presale')) ||
    location.pathname === '/login' ||
    location.pathname === '/auth/callback' ||
    location.pathname === '/reset-password';

  return (
    <div className="min-h-screen bg-maslow-cream flex flex-col overflow-x-hidden w-full max-w-full">
      <ScrollToTop />
      <a href="#main"
         className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-3 focus:left-3 focus:bg-maslow-navy focus:text-maslow-cream focus:px-4 focus:py-2 focus:rounded-sm">
        Skip to content
      </a>
      {!isHideHeaderPath && <Header setIsCartOpen={setIsCartOpen} />}

      <main id="main" className="flex-grow">
        <Routes>
          {/* PRE-LAUNCH: public routes are /, auth pages, /hull, /rooms, /maslow, /field-notes and the legal pages. Everything else is founder-gated via showFounderRoutes and falls through to the catch-all for non-founders. */}
          <Route path="/" element={<HomePage />} />
          {/* Back-door admin auth routes — not linked from any nav, but functional via direct URL. */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/callback" element={<AuthCallbackPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Public content routes — lifted out of the founder gate (R1). */}
          <Route path="/hull" element={<HullPage />} />
          <Route path="/rooms" element={<RoomsPage />} />
          <Route path="/suites" element={<Navigate to="/rooms" replace />} />
          <Route path="/sanctuary" element={<Navigate to="/hull" replace />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
          {/* The presale Sales Agreement (§16 checkbox target). Gated like the checkout until PRESALE_VISIBLE. */}
          <Route path="/sales-agreement" element={<SalesAgreementPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/cookie-policy" element={<CookiePolicyPage />} />
          <Route path="/field-notes" element={<FieldNotesIndex />} />
          <Route path="/field-notes/:slug" element={<FieldNotePost />} />
          <Route path="/maslow" element={<MaslowPage />} />
          <Route path="/faq" element={<FAQPage />} />

          {/* Account routes — always mounted for any signed-in user. ProtectedRoute
              redirects logged-out visitors to /login. NOT founder-gated. */}
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/profile/settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />

          {/* Presale / founding-member capture page (own header/footer, wired to the
              join_waitlist RPC; /presale aliases /build). Public only when
              BUILD_PAGE_PUBLIC is true — otherwise founder-gated in the block below. */}
          {/* /build and /presale are PUBLIC and show BuildPublicPage: what Maslow is,
              the plan, and the waitlist. No prices, no tiers, no buy buttons — the street
              cards and the Instagram bio point here. The real presale page with pricing is
              founder-only at /build/preview until BUILD_PAGE_PUBLIC flips to true. */}
          <Route path="/build" element={BUILD_PAGE_PUBLIC ? <BuildPage /> : <BuildPublicPage />} />
          <Route path="/presale" element={BUILD_PAGE_PUBLIC ? <BuildPage /> : <BuildPublicPage />} />
          {/* Presale checkout. Login required; inside, usePresaleAccess decides who may buy:
              everyone once PRESALE_VISIBLE is true, until then admins and presale_tester accounts only.
              The page refuses to render a Pay button if PRESALE_VISIBLE is on without PRESALE_TERMS_URL. */}
          <Route path="/build/checkout/:slug" element={<ProtectedRoute><PresaleCheckoutPage /></ProtectedRoute>} />

          {/* Admin-only routes — live during pre-launch for founder access. */}
          <Route path="/admin" element={<ProtectedRoute requireFounder={true}><AdminFundingDashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute requireFounder={true}><Dashboard /></ProtectedRoute>} />
          <Route path="/admin/field-research" element={<ProtectedRoute requireFounder={true}><FieldResearchPage /></ProtectedRoute>} />
          <Route path="/admin/research-results" element={<ProtectedRoute requireFounder={true}><FieldResearchResultsPage /></ProtectedRoute>} />

          {/* Founder-only preview of the full site during pre-launch. Non-admins never see these — they fall through to the catch-all and land on "/". */}
          {showFounderRoutes && (
            <>
              <Route path="/go" element={<GoPage />} />
              <Route path="/model" element={<RevenueModelPage />} />
              {/* Presale page with pricing — founder-only preview while BUILD_PAGE_PUBLIC is false. */}
              {!BUILD_PAGE_PUBLIC && <Route path="/build/preview" element={<BuildPage />} />}
              {CREDITS_VISIBLE && <Route path="/buy-credits" element={<ProtectedRoute><BuyCreditsPage /></ProtectedRoute>} />}

              <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />

              <Route path="/store" element={<ProtectedRoute><StorePage /></ProtectedRoute>} />
              <Route path="/product/:id" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />
              <Route path="/checkout-success" element={<ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute>} />
              <Route path="/locations/:slug" element={<ProtectedRoute><LocationDetail /></ProtectedRoute>} />


              <Route path="/prototypes" element={<ProtectedRoute requireFounder={true}><PrototypesPage /></ProtectedRoute>} />
              <Route path="/prototypes/system/:id" element={<ProtectedRoute requireFounder={true}><PrototypeSystemDetailPage /></ProtectedRoute>} />
              <Route path="/prototypes/prototype/:id" element={<ProtectedRoute requireFounder={true}><PrototypeDetailPage /></ProtectedRoute>} />
              <Route path="/prototypes/shopping-cart" element={<ProtectedRoute requireFounder={true}><PrototypeShoppingCartPage /></ProtectedRoute>} />
              <Route path="/prototypes/boxes" element={<ProtectedRoute requireFounder={true}><PrototypeBoxViewPage /></ProtectedRoute>} />
              <Route path="/prototypes/shopping" element={<ProtectedRoute requireFounder={true}><PrototypeShoppingListPage /></ProtectedRoute>} />
            </>
          )}

          <Route path="*" element={authResolving ? <div className="min-h-screen bg-maslow-cream" /> : <Navigate to="/" replace />} />
        </Routes>

        <Analytics />
        <SpeedInsights />
      </main>

      {!isHideHeaderPath && <Footer />}
      <ShoppingCart isCartOpen={isCartOpen} setIsCartOpen={setIsCartOpen} />
      <Toaster />
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AccessibilityProvider>
        <StripeProvider>
          <CartProvider>
            <Router>
              <Helmet>
                <title>Maslow — Premium Private Restrooms in SoHo</title>
                <meta name="description" content="Private, app-controlled restrooms in SoHo, New York City. Book by the session. Walk up or reserve in advance. Premium is the floor, not an upgrade." />
              </Helmet>
              <AppContent />
            </Router>
          </CartProvider>
        </StripeProvider>
      </AccessibilityProvider>
    </AuthProvider>
  );
};

export default App;
